// ClinicalNoteType enum mirror — keep in sync with appmed.DataLayer/Enums/ClinicalNoteType.cs
export enum ClinicalNoteType {
  PhysicalExamination = 1,
  MedicalHistory = 2,
  Counselling = 3,
}

export interface IScenarioSummary {
  id: number;
  nameEn: string;
  nameFr: string;
  isActive: number;
  slotCount: number;
  createdDate: string;
  lastModifiedDate: string;
}

export interface IScenarioWrite {
  nameEn: string;
  nameFr: string;
  description: string;
  isActive: number;
}

export interface IScenarioQuestionnaire {
  id: number;
  questionnaireID: number;
  shortNameEn: string;
  shortNameFr: string;
  orderIndex: number;
}

export interface IScenarioJournal {
  id: number;
  journalID: number;
  nameEn: string;
  nameFr: string;
  orderIndex: number;
}

export interface IScenarioClinicalNoteType {
  id: number;
  noteTypeID: ClinicalNoteType;
  questionnaireID: number | null;
  questionnaireShortNameEn: string | null;
  orderIndex: number;
}

export interface IScenarioQuestionnaireEntry {
  id: number;
  questionnaireID: number;
  offsetDays: number;
  timeOfDay: string | null;
  scoresJson: string | null;
  answersJson: string | null;
}

export interface IScenarioPhysiologicalEntry {
  id: number;
  physiologicalTypeID: number;
  unitID: number | null;
  offsetDays: number;
  timeOfDay: string | null;
  value: number;
}

export interface IScenarioLabTestEntry {
  id: number;
  biomarkerSpecimenID: number;
  offsetDays: number;
  timeOfDay: string | null;
  value: number;
}

export interface IScenarioJournalEntry {
  id: number;
  journalID: number;
  offsetDays: number;
  timeOfDay: string | null;
  responsesJson: string | null;
}

export interface IScenarioClinicalNoteEntry {
  id: number;
  noteTypeID: ClinicalNoteType;
  questionnaireID: number | null;
  offsetDays: number;
  timeOfDay: string | null;
  freeTextNote: string | null;
  answersJson: string | null;
}

export interface IScenarioLifeEventEntry {
  id: number;
  offsetDays: number;
  timeOfDay: string | null;
  title: string;
  description: string | null;
}

export interface IScenarioDocumentEntry {
  id: number;
  offsetDays: number;
  timeOfDay: string | null;
  documentName: string;
  filename: string | null;
}

export interface IScenarioPrescriptionEntry {
  id: number;
  startOffsetDays: number;
  endOffsetDays: number | null;
  drugName: string;
  dose: number;
  doseFrequencyID: number;
  doseFrequencyName: string | null;
  unitID: number;
  unitName: string | null;
}

export interface IScenarioPatientSlot {
  id: number;
  label: string;
  description: string | null;
  medicalNote: string | null;
  orderIndex: number;
  questionnaireEntries: IScenarioQuestionnaireEntry[];
  physiologicalEntries: IScenarioPhysiologicalEntry[];
  labTestEntries: IScenarioLabTestEntry[];
  journalEntries: IScenarioJournalEntry[];
  clinicalNoteEntries: IScenarioClinicalNoteEntry[];
  lifeEventEntries: IScenarioLifeEventEntry[];
  documentEntries: IScenarioDocumentEntry[];
  prescriptionEntries: IScenarioPrescriptionEntry[];
}

export interface IScenarioDetail {
  id: number;
  nameEn: string;
  nameFr: string;
  description: string | null;
  isActive: number;
  createdDate: string;
  lastModifiedDate: string;
  questionnaires: IScenarioQuestionnaire[];
  journals: IScenarioJournal[];
  clinicalNoteTypes: IScenarioClinicalNoteType[];
  slots: IScenarioPatientSlot[];
}

export interface IScenarioPatientSlotWrite {
  label: string;
  description: string | null;
  medicalNote: string | null;
  orderIndex: number;
}

export interface IScenarioQuestionnaireEntryWrite {
  questionnaireID: number;
  offsetDays: number;
  timeOfDay: string | null;
  scoresJson: string | null;
  answersJson: string | null;
}

export interface IScenarioPhysiologicalEntryWrite {
  physiologicalTypeID: number;
  unitID: number | null;
  offsetDays: number;
  timeOfDay: string | null;
  value: number;
}

export interface IScenarioLabTestEntryWrite {
  biomarkerSpecimenID: number;
  offsetDays: number;
  timeOfDay: string | null;
  value: number;
}

export interface IScenarioJournalEntryWrite {
  journalID: number;
  offsetDays: number;
  timeOfDay: string | null;
  responsesJson: string | null;
}

export interface IScenarioClinicalNoteEntryWrite {
  noteTypeID: ClinicalNoteType;
  questionnaireID: number | null;
  offsetDays: number;
  timeOfDay: string | null;
  freeTextNote: string | null;
  answersJson: string | null;
}

export interface IListItem {
  id: number;
  name: string;
  nameFr?: string;
}

export interface IScenarioLookups {
  physiologicalTypes: IListItem[];
  biomarkerSpecimens: IListItem[];
}

export interface IScenarioUserSearchResult {
  userID: number;
  patientID: number;
  firstName: string;
  lastName: string;
  email: string;
}

export interface IScenarioApplyMapping {
  slotID: number;
  userID: number;
}

export interface IScenarioApplyRequest {
  startDate: string; // ISO
  mappings: IScenarioApplyMapping[];
}

export interface IScenarioApplyResult {
  slotID: number;
  userID: number;
  patientID: number | null;
  success: boolean;
  error: string | null;
  insertedRowCount: number;
}

export interface IScenarioApplySummary {
  scenarioID: number;
  startDate: string;
  results: IScenarioApplyResult[];
}
